import { motion } from "framer-motion";

/* ============================================================
   Realistic chemistry-inspired molecule SVGs.
   Each component renders a different recognisable structure.
   ============================================================ */

const Atom = ({ cx, cy, r = 4, fill = "#10B981", stroke = "rgba(16,185,129,0.4)", pulse = false }) => (
  <g>
    {pulse && (
      <circle cx={cx} cy={cy} r={r * 2.4} fill={fill} opacity="0.18">
        <animate attributeName="r" values={`${r * 1.6};${r * 2.6};${r * 1.6}`} dur="3s" repeatCount="indefinite" />
        <animate attributeName="opacity" values="0.05;0.25;0.05" dur="3s" repeatCount="indefinite" />
      </circle>
    )}
    <circle cx={cx} cy={cy} r={r} fill={fill} stroke={stroke} strokeWidth="0.5" />
  </g>
);

const Bond = ({ x1, y1, x2, y2, color = "rgba(16,185,129,0.55)", w = 1.2, double = false }) => (
  <g stroke={color} strokeWidth={w}>
    <line x1={x1} y1={y1} x2={x2} y2={y2} />
    {double && (
      <line
        x1={x1 + 2}
        y1={y1 + 2}
        x2={x2 + 2}
        y2={y2 + 2}
        opacity="0.7"
      />
    )}
  </g>
);

/* 1. Benzene ring with alternating double bonds */
export const Benzene = ({ color = "#10B981" }) => {
  const r = 38;
  const cx = 50, cy = 50;
  const pts = Array.from({ length: 6 }, (_, i) => {
    const a = (Math.PI / 3) * i - Math.PI / 2;
    return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
  });
  return (
    <svg viewBox="0 0 100 100" className="w-full h-full">
      <g>
        {pts.map((p, i) => {
          const next = pts[(i + 1) % 6];
          return (
            <Bond
              key={i}
              x1={p.x}
              y1={p.y}
              x2={next.x}
              y2={next.y}
              color={`${color}88`}
              w={1.4}
              double={i % 2 === 0}
            />
          );
        })}
        <circle cx={cx} cy={cy} r={r * 0.55} fill="none" stroke={`${color}55`} strokeDasharray="2 3" />
        {pts.map((p, i) => (
          <Atom key={i} cx={p.x} cy={p.y} r={3.2} fill={color} pulse={i === 0} />
        ))}
      </g>
    </svg>
  );
};

/* 2. DNA helix segment (rungs + sidebands) */
export const DNAHelix = ({ color = "#10B981" }) => {
  const rungs = 6;
  const w = 100, h = 100;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-full">
      <defs>
        <linearGradient id="dnaA" x1="0" x2="1" y1="0" y2="0">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="50%" stopColor={color} stopOpacity="0.9" />
          <stop offset="100%" stopColor={color} stopOpacity="0.3" />
        </linearGradient>
      </defs>
      {Array.from({ length: rungs }).map((_, i) => {
        const t = i / (rungs - 1);
        const y = 12 + t * 76;
        const phase = Math.sin(t * Math.PI * 2);
        const x1 = 50 + phase * 30;
        const x2 = 50 - phase * 30;
        return (
          <g key={i}>
            <line x1={x1} y1={y} x2={x2} y2={y} stroke={`${color}88`} strokeWidth="1.2" />
            <circle cx={x1} cy={y} r="3" fill={color} />
            <circle cx={x2} cy={y} r="3" fill="#fff" opacity="0.85" />
          </g>
        );
      })}
      <path
        d="M 20 12 Q 50 30, 80 50 T 20 88"
        fill="none"
        stroke="url(#dnaA)"
        strokeWidth="1.4"
      />
      <path
        d="M 80 12 Q 50 30, 20 50 T 80 88"
        fill="none"
        stroke="url(#dnaA)"
        strokeWidth="1.4"
      />
    </svg>
  );
};

/* 3. Water (H2O) — 1 oxygen + 2 hydrogens */
export const Water = ({ color = "#10B981" }) => (
  <svg viewBox="0 0 100 100" className="w-full h-full">
    <Bond x1="50" y1="50" x2="22" y2="76" color={`${color}aa`} w={1.6} />
    <Bond x1="50" y1="50" x2="78" y2="76" color={`${color}aa`} w={1.6} />
    <Atom cx="50" cy="50" r={11} fill={color} pulse />
    <Atom cx="22" cy="76" r={6} fill="#fff" />
    <Atom cx="78" cy="76" r={6} fill="#fff" />
    <text x="48" y="54" fontSize="9" fill="#03150E" fontFamily="Outfit" fontWeight="700">O</text>
    <text x="19" y="80" fontSize="6" fill="#03150E" fontFamily="Outfit" fontWeight="700">H</text>
    <text x="75" y="80" fontSize="6" fill="#03150E" fontFamily="Outfit" fontWeight="700">H</text>
  </svg>
);

/* 4. Glucose-like 6-ring with hydroxyl groups */
export const Glucose = ({ color = "#10B981" }) => {
  const r = 30, cx = 50, cy = 50;
  const pts = Array.from({ length: 6 }, (_, i) => {
    const a = (Math.PI / 3) * i - Math.PI / 2;
    return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
  });
  return (
    <svg viewBox="0 0 100 100" className="w-full h-full">
      {pts.map((p, i) => {
        const next = pts[(i + 1) % 6];
        return <Bond key={i} x1={p.x} y1={p.y} x2={next.x} y2={next.y} color={`${color}aa`} w={1.4} />;
      })}
      {pts.map((p, i) => {
        const a = (Math.PI / 3) * i - Math.PI / 2;
        const ox = p.x + Math.cos(a) * 12;
        const oy = p.y + Math.sin(a) * 12;
        return (
          <g key={i}>
            <line x1={p.x} y1={p.y} x2={ox} y2={oy} stroke={`${color}66`} strokeWidth="1" />
            <circle cx={ox} cy={oy} r="2.5" fill="#fff" opacity="0.85" />
          </g>
        );
      })}
      {pts.map((p, i) => (
        <Atom key={i} cx={p.x} cy={p.y} r={3.4} fill={color} pulse={i === 2} />
      ))}
    </svg>
  );
};

/* 5. Peptide / amino acid chain */
export const PeptideChain = ({ color = "#10B981" }) => {
  const xs = [10, 28, 46, 64, 82];
  const ys = [60, 40, 60, 40, 60];
  return (
    <svg viewBox="0 0 100 100" className="w-full h-full">
      {xs.slice(0, -1).map((x, i) => (
        <Bond
          key={i}
          x1={x}
          y1={ys[i]}
          x2={xs[i + 1]}
          y2={ys[i + 1]}
          color={`${color}aa`}
          w={1.4}
        />
      ))}
      {xs.map((x, i) => (
        <g key={i}>
          {/* side group */}
          <line x1={x} y1={ys[i]} x2={x} y2={ys[i] + (i % 2 ? -16 : 16)} stroke={`${color}55`} strokeWidth="1" />
          <circle cx={x} cy={ys[i] + (i % 2 ? -16 : 16)} r="2.4" fill="#fff" opacity="0.8" />
          <Atom cx={x} cy={ys[i]} r={3.6} fill={i % 2 ? "#fff" : color} pulse={i === 2} />
        </g>
      ))}
    </svg>
  );
};

/* 6. Antioxidant cluster (radical scavenger - star shape) */
export const Antioxidant = ({ color = "#10B981" }) => {
  const cx = 50, cy = 50, r = 28;
  const arms = 8;
  return (
    <svg viewBox="0 0 100 100" className="w-full h-full">
      {Array.from({ length: arms }).map((_, i) => {
        const a = (Math.PI * 2 * i) / arms;
        const x = cx + r * Math.cos(a);
        const y = cy + r * Math.sin(a);
        return (
          <g key={i}>
            <Bond x1={cx} y1={cy} x2={x} y2={y} color={`${color}88`} w={1.2} />
            <circle cx={x} cy={y} r="3" fill={i % 2 ? "#fff" : color} opacity="0.9" />
          </g>
        );
      })}
      <circle cx={cx} cy={cy} r="6" fill={color}>
        <animate attributeName="r" values="5;7;5" dur="2.5s" repeatCount="indefinite" />
      </circle>
      <circle cx={cx} cy={cy} r="14" fill="none" stroke={`${color}44`} strokeDasharray="2 4">
        <animateTransform attributeName="transform" type="rotate" from="0 50 50" to="360 50 50" dur="20s" repeatCount="indefinite" />
      </circle>
    </svg>
  );
};

/* 7. Steroid / vitamin D ring system (3 fused rings) */
export const Steroid = ({ color = "#10B981" }) => (
  <svg viewBox="0 0 110 100" className="w-full h-full">
    <g stroke={`${color}aa`} strokeWidth="1.4" fill="none">
      <polygon points="20,55 35,40 50,55 35,70" />
      <polygon points="50,55 65,40 80,55 65,70" />
      <polygon points="80,40 92,32 100,46 88,54" />
    </g>
    <line x1="65" y1="40" x2="65" y2="22" stroke={`${color}88`} strokeWidth="1.2" />
    <circle cx="65" cy="22" r="3" fill="#fff" />
    <circle cx="35" cy="40" r="3" fill={color} />
    <circle cx="35" cy="70" r="3" fill={color} />
    <circle cx="65" cy="70" r="3" fill={color} />
    <circle cx="92" cy="32" r="3" fill="#fff" />
    <circle cx="100" cy="46" r="3" fill={color}>
      <animate attributeName="r" values="2.5;4;2.5" dur="2.5s" repeatCount="indefinite" />
    </circle>
  </svg>
);

/* 8. Polysaccharide chain (oligosaccharide) */
export const Polysaccharide = ({ color = "#10B981" }) => {
  const rings = 3;
  return (
    <svg viewBox="0 0 130 70" className="w-full h-full">
      {Array.from({ length: rings }).map((_, k) => {
        const cx = 20 + k * 45;
        const cy = 35;
        const r = 14;
        const pts = Array.from({ length: 6 }, (_, i) => {
          const a = (Math.PI / 3) * i - Math.PI / 2;
          return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
        });
        return (
          <g key={k}>
            {pts.map((p, i) => {
              const next = pts[(i + 1) % 6];
              return <Bond key={i} x1={p.x} y1={p.y} x2={next.x} y2={next.y} color={`${color}aa`} w={1.2} />;
            })}
            {pts.map((p, i) => (
              <circle key={i} cx={p.x} cy={p.y} r="2.4" fill={i === 0 ? "#fff" : color} />
            ))}
          </g>
        );
      })}
      {/* Glycosidic bonds between rings */}
      <line x1="34" y1="35" x2="51" y2="35" stroke={`${color}66`} strokeWidth="1.2" />
      <line x1="79" y1="35" x2="96" y2="35" stroke={`${color}66`} strokeWidth="1.2" />
    </svg>
  );
};

/* 9. Methane / tetrahedral SP3 */
export const Tetrahedral = ({ color = "#10B981" }) => (
  <svg viewBox="0 0 100 100" className="w-full h-full">
    <Bond x1="50" y1="50" x2="20" y2="20" color={`${color}aa`} w={1.4} />
    <Bond x1="50" y1="50" x2="80" y2="20" color={`${color}aa`} w={1.4} />
    <Bond x1="50" y1="50" x2="20" y2="80" color={`${color}aa`} w={1.4} />
    <Bond x1="50" y1="50" x2="80" y2="80" color={`${color}aa`} w={1.4} />
    <Atom cx="50" cy="50" r={8} fill={color} pulse />
    <Atom cx="20" cy="20" r={5} fill="#fff" />
    <Atom cx="80" cy="20" r={5} fill="#fff" />
    <Atom cx="20" cy="80" r={5} fill="#fff" />
    <Atom cx="80" cy="80" r={5} fill="#fff" />
  </svg>
);

/* 10. Lipid / fatty acid double chain */
export const Lipid = ({ color = "#10B981" }) => (
  <svg viewBox="0 0 120 80" className="w-full h-full">
    <g stroke={`${color}99`} strokeWidth="1.3" fill="none">
      <polyline points="10,30 22,20 34,30 46,20 58,30 70,20 82,30 94,20 106,30" />
      <polyline points="10,55 22,45 34,55 46,45 58,55 70,45 82,55 94,45 106,55" />
    </g>
    <circle cx="10" cy="30" r="6" fill={color} />
    <circle cx="10" cy="55" r="6" fill={color} />
    {[22, 34, 46, 58, 70, 82, 94, 106].map((x, i) => (
      <g key={x}>
        <circle cx={x} cy={i % 2 ? 30 : 20} r="2" fill="#fff" opacity="0.7" />
        <circle cx={x} cy={i % 2 ? 55 : 45} r="2" fill="#fff" opacity="0.7" />
      </g>
    ))}
  </svg>
);

/* ============================================================
   Background field of moving molecules. On parent hover,
   scales up uniformly via group-hover.
   ============================================================ */
const MOLECULES = [
  { Cmp: Benzene, color: "#10B981", size: 180 },
  { Cmp: DNAHelix, color: "#34D399", size: 220 },
  { Cmp: Water, color: "#10B981", size: 120 },
  { Cmp: Glucose, color: "#A7F3D0", size: 200 },
  { Cmp: Antioxidant, color: "#10B981", size: 240 },
  { Cmp: Steroid, color: "#34D399", size: 200 },
  { Cmp: Polysaccharide, color: "#10B981", size: 220 },
  { Cmp: PeptideChain, color: "#A7F3D0", size: 200 },
];

const POSITIONS = [
  { x: "5%", y: "8%" },
  { x: "70%", y: "5%" },
  { x: "15%", y: "55%" },
  { x: "82%", y: "60%" },
  { x: "45%", y: "30%" },
  { x: "60%", y: "78%" },
  { x: "30%", y: "82%" },
  { x: "88%", y: "30%" },
];

export const MoleculeField = ({ scaleOnGroupHover = true }) => {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {MOLECULES.map((m, i) => {
        const pos = POSITIONS[i % POSITIONS.length];
        const Cmp = m.Cmp;
        return (
          <motion.div
            key={i}
            initial={{ opacity: 0 }}
            animate={{
              opacity: [0.25, 0.55, 0.35, 0.55, 0.25],
              x: [0, 24, -18, 30, 0],
              y: [0, -22, 26, -14, 0],
              rotate: [0, 18, -10, 12, 0],
            }}
            transition={{
              duration: 18 + i * 2,
              delay: i * 0.6,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            style={{ left: pos.x, top: pos.y, width: m.size, height: m.size }}
            className={`absolute transition-transform duration-[1200ms] ease-out ${
              scaleOnGroupHover ? "group-hover/section:scale-[1.45]" : ""
            }`}
          >
            <Cmp color={m.color} />
          </motion.div>
        );
      })}
    </div>
  );
};

/* Map product code → corresponding molecule component */
export const PRODUCT_MOLECULE = {
  "01": Benzene,        // Protein isolate (peptide bonds → benzene-like aromatic ring)
  "02": Antioxidant,    // I&G
  "03": DNAHelix,       // CMP
  "04": DNAHelix,       // UMP
  "05": Polysaccharide, // XOS
  "06": Antioxidant,    // Glutathione
  "07": Steroid,        // Vitamin D2
  "08": Glucose,        // MOS
  "09": Polysaccharide, // Beta-Glucan
  "10": PeptideChain,   // Designer Peptides
};

export default MoleculeField;
